import { type ShoppingCartLineDto } from "@lib/ShiOneClient";

/**
 * Cart monetary totals. Freight, tax and recycling fees are not calculated from the cart yet;
 * they are currently hardcoded placeholders (see computeCartTotals) so we can verify the values
 * flow through checkout to Commerce end to end. Checkout sends these to Commerce, which persists
 * them on the web order.
 */
export interface CartTotals {
  subtotal: number;
  estFreight: number;
  estTax: number;
  recyclingFee: number;
  total: number;
}

/**
 * Subtotal for a single cart line: base product price plus every attached warranty. A line with
 * no quantity of its own is priced at qty 1 so the math matches how CartItemsTable renders it.
 */
export function computeCartLineTotal(line: ShoppingCartLineDto): number {
  const quantity = line.quantity ?? 1;
  const lineTotal = Number(line.unitPrice ?? 0) * quantity;
  const warrantyTotal = (line.warranties ?? []).reduce(
    (sum, warranty) =>
      sum + Number(warranty.unitPrice ?? 0) * (warranty.quantity ?? quantity),
    0,
  );
  return lineTotal + warrantyTotal;
}

/** Cart subtotal: the sum of every line total, including attached warranties. */
export function computeCartSubtotal(lines: ShoppingCartLineDto[]): number {
  return lines.reduce((sum, line) => sum + computeCartLineTotal(line), 0);
}

/**
 * Derives the monetary values sent to Commerce at checkout from the cart.
 *
 * TODO(SYN-778): freight, tax and recycling are not yet calculated from the cart. The
 * shoppingcartv2 GetShoppingCartItemList response (and the cart page) does not expose them, so
 * they are hardcoded to distinct placeholder values (10 / 20 / 30) purely to verify they flow
 * through checkout to the Commerce web order. Replace with a real pricing source before release.
 */
export function computeCartTotals(lines: ShoppingCartLineDto[]): CartTotals {
  const subtotal = computeCartSubtotal(lines);
  const estFreight = 10;
  const recyclingFee = 20;
  const estTax = 30;
  return {
    subtotal,
    estFreight,
    estTax,
    recyclingFee,
    total: subtotal + estFreight + estTax + recyclingFee,
  };
}
